import { motion } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const STATES = ["Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal","Other"];
const empty = { name: "", phone: "", email: "", state: "", city: "", current_business: "", message: "" };

export default function FranchiseForm() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required."); return; }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => String(v).trim() !== ""));
      await api.post("/leads", payload);
      toast.success("Enquiry received! Our franchise team will call you within 24 hours.");
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <section id="franchise" className="bg-[#f9f8f5] py-20 md:py-28" data-testid="franchise-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-[#e7e0d6] bg-white card-shadow-lg">
          <div className="grid lg:grid-cols-2">
            {/* Left — value */}
            <div className="relative bg-[#1f5d3a] p-9 md:p-12">
              <div className="eyebrow text-[#bfe0cd]">Become a Partner</div>
              <h2 className="mt-3 font-serif text-4xl leading-tight tracking-tight text-white md:text-5xl">
                Apply for your <span className="italic text-[#f2c39a]">franchise</span> today
              </h2>
              <p className="mt-4 max-w-md text-[15px] leading-relaxed text-[#d6e6dc]">
                Fill in your details and a senior member of our franchise team will personally call you to discuss monopoly rights, pricing and your first order.
              </p>
              <ul className="mt-8 space-y-4">
                {["Monopoly rights in your district","Free marketing & promotional kit","Direct-from-plant PCD pricing","Personal call within 24 hours"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-[15px] text-white">
                    <CheckCircle2 size={19} className="shrink-0 text-[#8fd3ab]" /> {t}
                  </li>
                ))}
              </ul>
              <div className="mt-10 rounded-xl bg-white/10 p-4 text-sm text-[#e6f0ea]">
                Also interested in <span className="font-semibold text-white">third-party / contract manufacturing?</span> Mention it in your message — we manufacture at our own GMP facility.
              </div>
            </div>

            {/* Right — form */}
            <motion.form onSubmit={submit}
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className="p-9 md:p-12" data-testid="franchise-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Full Name" required>
                  <input className="form-field" value={form.name} onChange={update("name")} placeholder="Your name" data-testid="franchise-name" />
                </Field>
                <Field label="Phone" required>
                  <input className="form-field" value={form.phone} onChange={update("phone")} placeholder="+91 XXXXX XXXXX" data-testid="franchise-phone" />
                </Field>
                <Field label="Email">
                  <input type="email" className="form-field" value={form.email} onChange={update("email")} placeholder="you@email.com" data-testid="franchise-email" />
                </Field>
                <Field label="State">
                  <select className="form-field" value={form.state} onChange={update("state")} data-testid="franchise-state">
                    <option value="">Select state</option>
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="City / District">
                  <input className="form-field" value={form.city} onChange={update("city")} placeholder="City" data-testid="franchise-city" />
                </Field>
                <Field label="Current Business">
                  <input className="form-field" value={form.current_business} onChange={update("current_business")} placeholder="Distributor, MR, retailer…" data-testid="franchise-business" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Message">
                    <textarea rows={3} className="form-field resize-none" value={form.message} onChange={update("message")} placeholder="Tell us about your requirement" data-testid="franchise-message" />
                  </Field>
                </div>
              </div>

              <button type="submit" disabled={submitting} data-testid="franchise-submit"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#d46a1d] px-7 py-4 text-base font-semibold text-white transition-transform hover:scale-[1.02] hover:bg-[#c05f16] disabled:cursor-not-allowed disabled:opacity-60">
                {submitting ? "Sending…" : "Submit Enquiry"}
                <ArrowRight size={18} />
              </button>
              <p className="mt-3 text-center text-xs text-[#8a8880]">By submitting, you agree to be contacted by Saans Wellness.</p>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-[#4a4a48]">
        {label} {required && <span className="text-[#d46a1d]">*</span>}
      </span>
      {children}
    </label>
  );
}
